import { useRoutes } from 'react-router-dom';
import { useSelector } from "react-redux";
import MainRoutes from './MainRoutes';
import TenancyAdminRoutes from './TenancyAdminRoutes';
import LoginRoutes from './LoginRoutes';
import UserRoutes from './UserRoutes';
import SyncRoutes from "./SyncRoutes";
import ProvisioningRoutes from "./ProvisioningRoutes";
import { BRAND_TYPE } from "../config";

const ThemeRoutes = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    const { isHierarchyTenancy, featureSwitches } = useSelector((state) => state.tenancy);

    // Feature switches are now centralized in Redux (fetched once in App.js)
    // Wrap in object with data property for backwards compatibility with existing code
    const featureSwitchesApiData = featureSwitches ? { data: featureSwitches } : null;

    // Display On-Demand, Reporting and Tenants tabs to users based on tenancy settings and user level permissions
    const getFilteredTenancyAdminRoutes = (user) => {
        return {
            ...TenancyAdminRoutes,
            children: TenancyAdminRoutes.children.filter(item => {
                if (item.path === 'settings' && user?.role?.includes('TenancyAdministrators')) {
                    return featureSwitchesApiData?.data?.enableSettings
                }
                if (item.path === 'journeybuilder' && user?.role?.includes('TenancyAdministrators')) {
                    return featureSwitchesApiData?.data?.enableJourneyBuilder
                }
                if (item.path === 'packs' && user?.role?.includes('TenancyAdministrators')) {
                    return featureSwitchesApiData?.data?.enableContentPacks
                }
                if (item.path === 'tenancy-simulations' && user?.role?.includes('TenancyAdministrators')) {
                    return featureSwitchesApiData?.data?.enableSimulation
                }
                if (item.path === 'reports') {
                    if (user?.role?.includes('TenancyAdministrators')) return user.enableReporting;
                    else return user.reportingLevel !== null && user.enableReporting;
                }
                if (item.path === 'reports-b') {
                    // Tenancy admins: unchanged — the route stays mounted and the page
                    // itself guards on the enableReportsB switch.
                    if (user?.role?.includes('TenancyAdministrators')) return true;
                    // Regular users (role === '') with a reportingLevel get a locked,
                    // self-scoped view (BD-901). Mirrors the 'reports' case but gates on
                    // the tenancy enableReportsB switch + boxphish brand (not enableReporting).
                    return BRAND_TYPE === 'boxphish'
                        && !!featureSwitchesApiData?.data?.enableReportsB
                        && user.reportingLevel != null && user.reportingLevel !== '';
                }
                if (item.path === 'on-demand') {
                    if (user?.role?.includes('TenancyAdministrators')) {
                        return user.enableOndemand;
                    }
                    else return user.onDemandLevel !== null && user.enableOndemand;
                }
                if (item.path === 'tenants') {
                    if (user?.role?.includes('PartnerAdministrators')) {
                        return true;
                    }
                    else if (user?.role?.includes('TenancyAdministrators')) {
                        return isHierarchyTenancy;
                    }
                    else return false
                }
                if (user?.role?.includes('TenancyAdministrators')) return true;
            })
        };
    };

    const getFilteredUserRoutes = () => {
        return {
            ...UserRoutes,
            children: UserRoutes.children.filter(item => {
                if (item.path === 'feed') {
                    return featureSwitchesApiData?.data?.enableNewsFeed
                }
                else {
                    return true
                }
            })
        };
    };

    let routes = [];

    if (user?.role === 'Administrators') {
        routes = [MainRoutes, TenancyAdminRoutes, UserRoutes, SyncRoutes, LoginRoutes, ProvisioningRoutes];
    } else if (user?.role?.includes('TenancyAdministrators')) {
        const filteredTenancyAdminRoutes = getFilteredTenancyAdminRoutes(user);
        const filteredUserRoutes = getFilteredUserRoutes();
        routes = [filteredTenancyAdminRoutes, filteredUserRoutes, SyncRoutes, LoginRoutes];
        if (user?.role?.includes('PartnerAdministrators')) routes.push(ProvisioningRoutes);
    } else if (user?.role === '') {
        const filteredTenancyAdminRoutes = getFilteredTenancyAdminRoutes(user);
        const filteredUserRoutes = getFilteredUserRoutes(user);

        routes = [filteredTenancyAdminRoutes, filteredUserRoutes, LoginRoutes];
    } else {
        routes = [LoginRoutes];
    }

    return useRoutes(routes);
};

export default ThemeRoutes;
